import {createAction, createAsyncThunk, createReducer} from '@reduxjs/toolkit';

import FormData from 'form-data';

import {ENDPOINTS} from 'constants/endpoints';
import {UpsellType, UpsellsStateType} from 'desktop/pages/success/types/upsellsTypes';
import {RouterType} from 'hostApplicationStoreType';
import {apiConfig} from 'libs/api/api';

import {getOnePageUpsellData} from '../order/order';

const MATCH = {
    coating: '82%',
    insurance: '92%',
    package: '87%',
}; // According to Nadav's comment in GUSA-5302

export const initialState: UpsellsStateType = {
    addedUpsell: null,
    error: '',
    isUpsellsDisabled: false,
    list: [],
    status: 'pending',
};

export type UpsellsResponseType = {
    upsells: Array<
        UpsellType & {
            old_price: number;
            parent_id: number;
            upsell_sku: string;
            copay_price: number;
            upsellId: number;
        }
    >;
};

type UpsellsAddResponseType = {
    upsellId: number;
    upsellType: string;
    increment_id: string;
    items: string[];
};

/**
 * Disable upsells
 * @param {boolean} disable - Upsells state
 * @returns {ActionType<boolean>} Action
 */
export const disableUpsells = createAction<boolean>('UPSELLS@DISABLE');

/**
 * Set active upsell for popup
 * @param {string} error - error
 * @return {ActionType<string>} Action
 */
export const setUpsellCustomError = createAction<string>('UPSELLS@SET_CUSTOM_ERROR');

/**
 * Add upsell from the Success page
 * @param {number} orderId - Order id
 * @param {UpsellType} upsell - Upsell to add
 * @param {string} paymentToken - Payment token
 * @returns {ActionType<AxiosPromise, {router: StoreRouterType}>} Action
 */
export const addUpsellFromSuccessPage = createAsyncThunk<
    UpsellsAddResponseType,
    {
        orderId: string;
        upsell: UpsellType;
        paymentToken: string;
        meta: {
            router: RouterType | null;
        };
    },
    {
        fulfilledMeta: {
            router: RouterType | null;
        };
    }
>(
    'UPSELLS@ADD_UPSELL_FROM_SUCCESS_PAGE',
    async ({meta, orderId, paymentToken, upsell}, {fulfillWithValue}) => {
        const data = new FormData();

        data.append('orderId', orderId);
        data.append('upsellId', String(upsell.id));
        data.append('upsellType', upsell.type);
        data.append('paymentToken', paymentToken);

        const response = await apiConfig.instanceGusaBackend({
            data,
            method: 'post',
            url: `${ENDPOINTS.UPSELL_ADD_FROM_SUCCESS_PAGE}`,
        });

        return fulfillWithValue(response.data, meta);
    },
);

/**
 * Reducer for success page
 * @param {UpsellsStateType} state - Reducer state
 * @param {ActionType<UpsellsResponseType | UpsellsAddResponseType>} action - Redux action
 * @return {UpsellsStateType} New reducer state
 */
export const upsellsReducer = createReducer<UpsellsStateType>(initialState, builder => {
    builder
        .addCase(getOnePageUpsellData.fulfilled, (state, action) => {
            if (!action.payload) {
                return {...state};
            }
            const {upsells} = action.payload;
            return {
                ...state,
                error: '',
                list: upsells
                    .map(
                        ({
                            copay_price: copayPrice,
                            old_price: oldPrice,
                            parent_id: parentId,
                            sku,
                            type,
                            upsell_sku: upsellSku,
                            ...upsell
                        }) => ({
                            ...upsell,
                            copayPrice,
                            isActive: true,
                            isAdded: false,
                            match: MATCH[type as keyof typeof MATCH],
                            oldPrice,
                            parentId,
                            sku,
                            type,
                            upsellSku,
                        }),
                    )
                    .sort((a, b) => (b.match || '').localeCompare(a.match || '')),
                status: 'fulfilled',
            };
        })
        .addCase(disableUpsells, (state, action) => ({
            ...state,
            isUpsellsDisabled: Boolean(action.payload),
        }))
        .addCase(addUpsellFromSuccessPage.fulfilled, (state, action) => ({
            ...state,
            addedUpsell: {
                id: action.payload.increment_id,
                sku: action.payload.items[0],
            },
            error: '',
            list: state.list.map((upsell: UpsellType): UpsellType => {
                const isAdded =
                    upsell.id === action.payload.upsellId &&
                    upsell.type === action.payload.upsellType;
                return upsell.isAdded ? upsell : {...upsell, isActive: !isAdded, isAdded};
            }),
            status: 'fulfilled',
        }))
        .addCase(addUpsellFromSuccessPage.pending, state => ({
            ...state,
            addedUpsell: null,
            status: 'pending',
        }))
        .addCase(addUpsellFromSuccessPage.rejected, state => ({
            ...state,
            error: 'There was an error processing your order',
            status: 'rejected',
        }))
        .addCase(setUpsellCustomError, (state, action) => ({
            ...state,
            error: String(action.payload),
        }));
});
